import _ from 'lodash'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import PrintIcon from '@mui/icons-material/Print'

import DeleteConfirmationDialog from '@/components/ui/dialogs/DeleteConfirmationDialog'
import Document from '@/components/group/Document'
import FilePreviewDialog from '@/components/ui/dialogs/FilePreviewDialog/FilePreviewDialog'
import UploadButton from '@/components/ui/buttons/UploadButton'
import { useDocumentsGridSize } from '@/hooks/useDocumentsGridSize'

export type DocumentsProps = {
  documents: Array<{
    filename: string
    key: string
    url: string
    canDelete?: boolean
  }>
  isDeleting?: boolean
  onDelete?: (documents: DocumentsProps['documents']) => void
  onSaveFileName?: (filename: string, key: string) => void
  onSaveFileAnnotation?: (file: File, key: string) => void
  onUpload?: (file: File) => void
  readOnly?: boolean
  showTitle?: boolean
}

export default function Documents(props: DocumentsProps) {
  const { documents, isDeleting, onDelete, onSaveFileName, onSaveFileAnnotation, onUpload, readOnly = false, showTitle = true } = props
  const { t } = useTranslation()

  const [deleteConfirmationDialogOpen, setDeleteConfirmationDialogOpen] = useState<boolean>(false)
  const [selectedDocuments, setSelectedDocuments] = useState<Record<string, DocumentsProps['documents'][number]>>({})
  const [previewDialogOpen, setPreviewDialogOpen] = useState<boolean>(false)
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState<number>(-1)

  const { documentsWrapperRef, gridSize } = useDocumentsGridSize()

  // reset selectedDocuments after delete
  useEffect(() => {
    if (!!_.keys(selectedDocuments).length && !_.intersectionBy(documents, _.values(selectedDocuments), 'key').length) {
      setSelectedDocuments({})
    }
  }, [documents, selectedDocuments])

  const handleChangeDocumentSelection = (document: DocumentsProps['documents'][number]) => (event: React.ChangeEvent, checked: boolean) => {
    setSelectedDocuments((prevSelectedDocuments) => {
      return !checked ? _.omit(prevSelectedDocuments, document.key) : { ...prevSelectedDocuments, [document.key]: document }
    })
  }

  const handleDelete = () => {
    onDelete?.(_.values(selectedDocuments))
  }

  const handleUpload = (file: File) => {
    onUpload?.(file)
  }

  const toggleDeleteConfirmationDialog = () => {
    setDeleteConfirmationDialogOpen((prev) => !prev)
  }

  const handleOpenPreview = (index: number) => {
    setCurrentPreviewIndex(index)
    setPreviewDialogOpen(true)
  }

  const handleClosePreview = () => {
    setPreviewDialogOpen(false)
    setCurrentPreviewIndex(-1)
  }

  const handleNextDocument = () => {
    if (currentPreviewIndex < documents.length - 1) {
      setCurrentPreviewIndex(currentPreviewIndex + 1)
    }
  }

  const handlePreviousDocument = () => {
    if (currentPreviewIndex > 0) {
      setCurrentPreviewIndex(currentPreviewIndex - 1)
    }
  }

  const handleSaveFileNameFromPreview = (filename: string) => {
    if (currentPreviewIndex >= 0) {
      const currentDoc = documents[currentPreviewIndex]
      onSaveFileName?.(filename, currentDoc.key)
    }
  }

  const handlePrint = () => {
    if (currentPreviewIndex < 0) return
    const currentDoc = documents[currentPreviewIndex]
    const printWindow = window.open(currentDoc.url, '_blank')
    if (printWindow) {
      printWindow.addEventListener('load', () => {
        printWindow.print()
      })
    }
  }

  const currentDocument = currentPreviewIndex >= 0 ? documents[currentPreviewIndex] : null
  const currentFileName = currentDocument ? _.split(currentDocument.filename, '.')[0] : ''
  const currentFileType = currentDocument ? _.last(_.split(currentDocument.filename, '.')) || '' : ''

  return (
    <Stack direction="column" spacing={2}>
      {(showTitle || !!_.keys(selectedDocuments).length) && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ position: 'relative' }}>
          {showTitle && (
            <Typography variant="h3" component="div" noWrap sx={{ color: '#6B7280', fontSize: '16px', fontWeight: 700 }}>
              {t('page.expertise.documents.title')}
            </Typography>
          )}
          {!!_.keys(selectedDocuments).length && (
            <Tooltip title={t('common:button.delete.tooltip')} placement="top">
              <Button
                color="error"
                disabled={isDeleting}
                onClick={toggleDeleteConfirmationDialog}
                size="small"
                variant="chip"
                sx={{ minWidth: 'unset', position: 'absolute', top: '50%', right: 0, transform: 'translateY(-50%)' }}
              >
                {isDeleting ? <CircularProgress size={18} /> : <DeleteRoundedIcon />}
              </Button>
            </Tooltip>
          )}
        </Stack>
      )}
      <Stack ref={documentsWrapperRef}>
        <Grid container spacing={3}>
          {!readOnly && (
            <Grid item xs={gridSize} alignItems="center">
              <UploadButton onUpload={handleUpload} />
            </Grid>
          )}
          {_.map(documents, (document, index) => (
            <Grid key={index} item xs={gridSize} alignItems="center">
              <Box sx={{ position: 'relative' }}>
                <Document
                  disableAnnotation={readOnly}
                  disableChangeName={readOnly}
                  extension={_.split(document.filename, '.')[1]}
                  fileKey={document.key}
                  name={_.split(document.filename, '.')[0]}
                  onOpenPreview={() => handleOpenPreview(index)}
                  onSaveFileAnnotation={onSaveFileAnnotation}
                  onSaveFileName={onSaveFileName}
                  src={document.url}
                />
                {!readOnly && !!document.canDelete && (
                  <Checkbox
                    checked={!!selectedDocuments[document.key]}
                    size="small"
                    sx={{ position: 'absolute', top: '0', right: '0' }}
                    onChange={handleChangeDocumentSelection(document)}
                  />
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Stack>
      <DeleteConfirmationDialog
        isDeleting={isDeleting}
        onClose={toggleDeleteConfirmationDialog}
        onDelete={handleDelete}
        open={deleteConfirmationDialogOpen}
        title={t('dialog.deleteConfirmation.documents.title', { count: _.keys(selectedDocuments).length })}
        message={t('dialog.deleteConfirmation.documents.message')}
      />
      {currentDocument && (
        <FilePreviewDialog
          disableChangeFileName={readOnly}
          fileName={currentFileName}
          fileType={currentFileType}
          fileKey={currentDocument.key}
          onClose={handleClosePreview}
          onSaveFileName={!readOnly ? handleSaveFileNameFromPreview : undefined}
          onNext={currentPreviewIndex < documents.length - 1 ? handleNextDocument : undefined}
          onPrevious={currentPreviewIndex > 0 ? handlePreviousDocument : undefined}
          open={previewDialogOpen}
          src={currentDocument.url}
        />
      )}
    </Stack>
  )
}
